import { UploadableMap } from "relay-runtime";
import _ from "lodash";
import { Buffer } from "buffer";
import { Platform } from "react-native";
import { notify } from "@lib/util/logger";

// iOS用のファイル型定義
interface IOSFileObject {
  uri?: string;
  type?: string;
  name?: string;
}

export async function makeUploadablesFromFileList(
  objectPath: string,
  files: File[],
  influencerId?: string
): Promise<UploadableMap> {
  // iOS向けの最適化: ファイルオブジェクトの検証とフォーマット修正
  if (Platform.OS === "ios") {
    const processedFiles: Array<[string, File]> = files.map((file, index) => {
      try {
        notify(
          {
            type: "PostDraft:Diagnostic:FileItem",
            phase: "FILE_ITEM_PROCESSING",
            platform: "ios",
            influencerId: influencerId ?? "unknown",
            index,
            fileInfo: {
              name: file?.name ?? "unknown",
              size: file?.size ?? 0,
              sizeMB:
                file?.size != null
                  ? (file.size / (1024 * 1024)).toFixed(2)
                  : "0",
              type: file?.type ?? "unknown",
            },
            timestamp: new Date().toISOString(),
          },
          { level: "debug" }
        ).catch(() => {
          // ログ送信失敗を無視（既存処理に影響させない）
        });
      } catch {
        // ログ処理の例外を完全に無視（既存処理を保護）
      }

      // iOSでのファイルオブジェクト構造の確認と修正
      if (file != null && typeof file === "object") {
        // ファイルオブジェクトに必要なプロパティが存在するか確認
        const fileWithMeta = file as unknown as IOSFileObject;

        // URIが存在し、file://プレフィックスが含まれている場合の処理
        if (fileWithMeta.uri != null && typeof fileWithMeta.uri === "string") {
          // file://プレフィックスを削除（必要に応じて）
          const cleanedUri = fileWithMeta.uri.replace("file://", "");

          // 新しいファイルオブジェクトを作成（iOSのFormData互換性向上）
          const processedFile = {
            ...file,
            uri: Platform.OS === "ios" ? cleanedUri : fileWithMeta.uri,
            type: fileWithMeta.type ?? "application/octet-stream",
            name: fileWithMeta.name ?? `upload_${Date.now()}_${index}`,
          } as unknown as File;

          return [`${objectPath}.${index}`, processedFile];
        }
      }
      return [`${objectPath}.${index}`, file];
    });

    return Object.fromEntries(processedFiles) as UploadableMap;
  }

  // Android/Web向けの既存実装
  const entries: Array<[string, File]> = Array.from(files, (file, i) => {
    try {
      notify(
        {
          type: "PostDraft:Diagnostic:FileItem",
          phase: "FILE_ITEM_PROCESSING",
          platform: Platform.OS,
          influencerId: influencerId ?? "unknown",
          index: i,
          fileInfo: {
            name: file?.name ?? "unknown",
            size: file?.size ?? 0,
            sizeMB:
              file?.size != null ? (file.size / (1024 * 1024)).toFixed(2) : "0",
            type: file?.type ?? "unknown",
          },
          timestamp: new Date().toISOString(),
        },
        { level: "debug" }
      ).catch(() => {
        // ログ送信失敗を無視（既存処理に影響させない）
      });
    } catch {
      // ログ処理の例外を完全に無視（既存処理を保護）
    }

    return [`${objectPath}.${i}`, file];
  });

  return Object.fromEntries<File>(entries);
}

export async function makeUploadablesFromFile(
  objectPath: string,
  file: File
): Promise<UploadableMap> {
  return Object.fromEntries<File>([[objectPath, file]]);
}

export function decodeId(encodedId: string): string {
  return <string>_.last(Buffer.from(encodedId, "base64").toString().split(":"));
}
